import React, { useState, useEffect } from "react";
import { downloadFile } from "../../../Constant/Constant";
import { FaRegFileExcel, FaTimes } from "react-icons/fa";
import { FiCheckCircle } from "react-icons/fi";
import { FiUpload } from "react-icons/fi";

const ApartmentBulkModel = ({
  open,
  onClose,
  setSelectedFile,
  selectedFile,
  setSelectedTower,
  selectedTower,
  onImport,
  fileLink,
  fileName,
  isTower = false,
  towers,
  onNextTower,
  onDone,
  currentTowerIndex,
  setCurrentTowerIndex,
  uploadedTowers = [],
  towerUploadStatus = {},
}) => {
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
      setSelectedFile(file);
      setError("");
    } else {
      setError("Please upload a valid Excel file (.xlsx or .xls)");
      setSelectedFile(null);
      e.target.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
      setSelectedFile(file);
      setError("");
    } else {
      setError("Please upload a valid Excel file (.xlsx or .xls)");
      setSelectedFile(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleImport = () => {
    if (!selectedFile) {
      setError("Please select a valid Excel file before importing.");
      return;
    }
    if (isTower && !selectedTower) {
      setError("Please select a tower before importing.");
      return;
    }
    if (uploadedTowers.includes(selectedTower?.value)) {
      setError("This tower already has a file uploaded.");
      return;
    }
    onImport(selectedFile, selectedTower?.value);
    setSelectedFile(null);
    if (currentTowerIndex < towers.length - 1) {
      onNextTower();
    }
  };

  const handleTowerSelect = (tower) => {
    setSelectedTower({
      value: tower.id,
      label: tower.towerName || `Tower ${tower.towerNum}`,
    });
    setCurrentTowerIndex(towers.findIndex((t) => t.id === tower.id));
    setSelectedFile(null);
    setError("");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-upload-title"
    >
      <div className="bg-white rounded-[10px] shadow-xl w-[800px] flex h-[600px] transform transition-all">
        {/* Left Tower List */}
        <div className="w-1/3 bg-gray-50 border-r rounded-[10px] border-gray-200 p-4 overflow-y-auto">
          <h3 className="text-lg font-medium text-gray-700 mb-4">Towers</h3>
          <div>
            {towers.map((tower, index) => {
              const isUploaded = uploadedTowers.includes(tower.id);
              const uploadStatus = towerUploadStatus[tower.id] || {};

              return (
                <div
                  key={tower.id}
                  className={`p-2 cursor-pointer border-2 border-slate-200 rounded-md mb-2 relative ${
                    selectedTower?.value === tower.id
                      ? "bg-customGreen text-white"
                      : isUploaded
                      ? "bg-gray-100 cursor-not-allowed"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handleTowerSelect(tower)}
                >
                  <div className="flex justify-between items-center">
                    <span>{tower.towerName || `Tower ${tower.towerNum}`}</span>
                    {isUploaded && (
                      <span
                        className={`${
                          selectedTower?.value === tower.id
                            ? "text-white "
                            : "text-green-500"
                        }`}
                      >
                        <FiCheckCircle className="h-5 w-5" />
                      </span>
                    )}
                  </div>

                  {/* Show upload status if available */}
                  {/* {isUploaded && uploadStatus.insertedCount !== undefined && (
                    <div className="text-xs mt-1 text-gray-600">
                      <span className="font-medium">Uploaded: </span>
                      {uploadStatus.insertedCount} units
                      {uploadStatus.skippedCount > 0 && (
                        <span className="text-red-500 ml-1">
                          ({uploadStatus.skippedCount} skipped)
                        </span>
                      )}
                    </div>
                  )} */}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right File Upload Section */}
        <div className="w-2/3 p-6 flex flex-col">
          {/* Header */}
          <div className="flex bg-slate-100 p-2 rounded-md justify-between items-center mb-6">
            <h2
              id="bulk-upload-title"
              className="text-lg font-medium text-green-700"
            >
              Bulk Upload for {selectedTower?.label || "Select a Tower"}
            </h2>
            <button
              onClick={onClose}
              className="text-red-600 bg-white px-2 focus:outline-none rounded-full p-1"
              aria-label="Close modal"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* File Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Excel File
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              } ${
                uploadedTowers.includes(selectedTower?.value)
                  ? "bg-gray-100 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={
                uploadedTowers.includes(selectedTower?.value)
                  ? undefined
                  : handleDrop
              }
              onClick={() => {
                if (!uploadedTowers.includes(selectedTower?.value)) {
                  document.getElementById("file-upload").click();
                }
              }}
            >
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploadedTowers.includes(selectedTower?.value)}
              />

              {selectedFile ? (
                <div className="flex flex-col items-center">
                  <FaRegFileExcel className="text-green-500 text-4xl mb-2" />
                  <p className="text-sm font-medium text-gray-700 truncate max-w-full">
                    {selectedFile.name}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="mt-2 text-sm text-red-500 hover:text-red-700"
                  >
                    Remove file
                  </button>
                </div>
              ) : uploadedTowers.includes(selectedTower?.value) ? (
                <div className="flex flex-col items-center">
                  <FiCheckCircle className="text-green-500 text-4xl mb-2" />
                  <p className="text-sm text-gray-600">
                    File already uploaded for this tower
                  </p>
                  {towerUploadStatus[selectedTower?.value] && (
                    <div className="text-xs mt-2">
                      <span className="font-medium">Status: </span>
                      {
                        towerUploadStatus[selectedTower?.value].insertedCount
                      }{" "}
                      units imported
                      {towerUploadStatus[selectedTower?.value].skippedCount >
                        0 && (
                        <span className="text-red-500 ml-1">
                          (
                          {towerUploadStatus[selectedTower?.value].skippedCount}{" "}
                          skipped)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <FiUpload className="text-gray-400 text-4xl mb-2" />
                  <p className="text-sm text-gray-600">
                    Drag & drop your file here or click to browse
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    (Only .xlsx or .xls files accepted)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="text-red-500 text-sm mb-4 text-center"
              id="file-error"
            >
              {error}
            </div>
          )}

          {/* Note & Template */}
          <div className="text-sm text-center text-gray-600 mb-6">
            <strong>Note:</strong> Only upload Excel file formats.{" "}
            <button
              onClick={() => downloadFile(fileLink, fileName)}
              className="text-blue-500 hover:underline"
            >
              Download Example Template
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border text-center border-green-100 rounded-lg p-3">
              <div className="text-green-800 font-medium text-sm">
                Successful
              </div>
              <div className="text-2xl font-bold text-green-600">
                {towerUploadStatus[selectedTower?.value]?.insertedCount || 0}
              </div>
              <div className="text-green-500 text-xs">units inserted</div>
            </div>
            <div className="bg-yellow-50 border text-center border-yellow-100 rounded-lg p-3">
              <div className="text-yellow-800 font-medium text-sm">Skipped</div>
              <div className="text-2xl font-bold text-yellow-600">
                {towerUploadStatus[selectedTower?.value]?.skippedCount || 0}
              </div>
              {towerUploadStatus[selectedTower?.value]?.skippedCount > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1 text-yellow-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Duplicate units found:
                  </h3>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Unit No.
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {towerUploadStatus[
                            selectedTower?.value
                          ]?.skippedUnitNos.map((unitNo, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                {unitNo}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex justify-center gap-3 mt-auto">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              aria-label="Cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={
                !selectedFile ||
                !selectedTower ||
                uploadedTowers.includes(selectedTower?.value)
              }
              className="px-6 py-3 bg-customGreen text-white rounded-md focus:outline-none focus:ring-2 text-sm disabled:opacity-50"
              aria-label="Import file"
            >
              {currentTowerIndex < towers.length - 1
                ? "Import & Next"
                : "Import"}
            </button>
            {currentTowerIndex === towers.length - 1 && (
              <button
                onClick={onDone}
                className="px-6 py-3 bg-customGreen text-white rounded-md focus:outline-none focus:ring-2 text-sm"
                aria-label="Done"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApartmentBulkModel;
